import React, { useState, useEffect } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock } from "lucide-react";
import { API } from "@/lib/api";
import { CUSTOMER_TOKEN_KEY } from "@/lib/constants";

const CustomerGate = ({ children }) => {
  const { nr, token } = useParams();
  const [authed, setAuthed] = useState(
    !!sessionStorage.getItem(CUSTOMER_TOKEN_KEY)
  );
  const [checking, setChecking] = useState(
    !!token && !sessionStorage.getItem(CUSTOMER_TOKEN_KEY)
  );
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (!token || sessionStorage.getItem(CUSTOMER_TOKEN_KEY)) {
        setChecking(false);
        return;
      }
      try {
        const res = await axios.get(`${API}/public/quote-access/${token}`);
        if (cancelled) return;
        if (res.data?.requires_password === false && res.data?.token) {
          sessionStorage.setItem(CUSTOMER_TOKEN_KEY, res.data.token);
          setAuthed(true);
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setChecking(false);
      }
    };
    check();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (authed) return children;

  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--brand-bg)]" data-testid="customer-gate-loading">
        <div className="h-8 w-8 rounded-full border-2 border-[var(--brand-primary)] border-t-transparent animate-spin" />
      </div>
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const res = await axios.post(`${API}/public/customer-login`, {
        password,
        angebot_nr: nr || "",
        token: token || "",
      });
      sessionStorage.setItem(CUSTOMER_TOKEN_KEY, res.data.token);
      setAuthed(true);
    } catch (e2) {
      const d = e2.response?.data?.detail;
      setErr(typeof d === "string" ? d : "Passwort falsch");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--brand-bg)] font-[Inter] px-4">
      <Card className="p-8 bg-white border-[var(--brand-border)] w-full max-w-md shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div
            className="h-12 w-12 rounded-xl flex items-center justify-center text-white font-bold text-xl"
            style={{ background: "var(--brand-primary)" }}
          >
            <Lock className="w-5 h-5" />
          </div>
          <div>
            <div className="font-[Playfair_Display] text-xl font-bold tracking-wide text-[var(--brand-ink)]">
              Geschütztes Angebot
            </div>
            <div className="text-[11px] uppercase tracking-[0.25em] text-[var(--brand-primary)]">
              {nr ? `Nr. ${nr}` : "Zugang"}
            </div>
          </div>
        </div>
        <p className="text-sm text-[var(--brand-muted)] mb-4">
          Bitte geben Sie das Zugangs-Passwort ein, das Sie zusammen mit dem
          Link erhalten haben.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label>Zugangs-Passwort</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoFocus
              data-testid="customer-password"
              required
            />
          </div>
          {err && (
            <div
              className="text-sm rounded p-2"
              style={{ background: "#FEF2F2", color: "#991B1B" }}
              data-testid="customer-error"
            >
              {err}
            </div>
          )}
          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-dark)] text-white"
            data-testid="customer-submit"
          >
            <Lock className="w-4 h-4 mr-1" />
            {loading ? "Prüfe…" : "Angebot anzeigen"}
          </Button>
        </form>
      </Card>
    </div>
  );
};







export { CustomerGate };
